import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, ClipReveal, FloorLabel, Img } from "../motion/Reveal";
import { formatDate } from "../../lib/api";
import { t } from "../../lib/i18n";

/** Stories from the companies: one lead story with room to breathe, three more as a quiet list. */
export const NewsFloor = ({ articles = [] }) => {
  const [lead, ...rest] = articles;
  if (!lead) return null;
  const cta = (a) => (a.category === "Mennesker" || a.category === "People" ? t("Les historien") : t("Les saken"));
  return (
    <section data-testid="floor-aktuelt" className="bg-white">
      <div className="wrap pb-[var(--floor)]">
        <Reveal className="hairline pt-[var(--floor)] grid-12 gap-y-6 items-end">
          <div className="col-span-12 lg:col-span-6">
            <FloorLabel>{t("Aktuelt")}</FloorLabel>
            <h2 className="mt-4 t-h2">{t("Fra selskapene. Fra industrien.")}</h2>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8">
            <p className="t-body text-muted max-w-[40ch]">{t("Menneskene, prosjektene og fagmiljøene som former EQVA.")}</p>
            <Link to="/aktuelt" data-testid="news-see-all" className="link-arrow mt-5 t-small">{t("Se alle saker")} <ArrowRight size={16} /></Link>
          </div>
        </Reveal>

        <div className="mt-14 md:mt-20 grid-12 gap-y-14">
          <Reveal soft as="article" data-testid="news-card-0" className="col-span-12 lg:col-span-7">
            <Link to={`/aktuelt/${lead.slug}`} className="group block">
              <ClipReveal className="aspect-[3/2] bg-surface img-zoom">
                <Img src={lead.image} alt={lead.image_alt || lead.title} sizes="(min-width:1024px) 58vw, 100vw" className="h-full w-full object-cover" />
              </ClipReveal>
              <div className="mt-8 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <p className="t-label text-accent">{lead.company}</p>
                <p className="t-small text-muted">{lead.category}{lead.published_at ? ` · ${formatDate(lead.published_at)}` : ""}</p>
              </div>
              <h3 className="mt-4 max-w-[22ch] text-[clamp(1.75rem,3vw,3rem)] font-medium leading-[1.08] tracking-[-0.025em] group-hover:text-blue transition-colors duration-200">{lead.title}</h3>
              {lead.lead && <p className="mt-5 max-w-[56ch] t-body text-muted">{lead.lead}</p>}
              <span className="link-arrow mt-6 t-small">{cta(lead)} <ArrowRight size={16} /></span>
            </Link>
          </Reveal>

          <div className="col-span-12 lg:col-span-5 lg:pl-10 xl:pl-16 lg:border-l lg:border-line">
            {rest.map((a, i) => (
              <Reveal key={a.slug} as="article" delay={i * 70} data-testid={`news-card-${i + 1}`} className={`${i ? "hairline" : ""} last:border-b last:border-line`}>
                <Link to={`/aktuelt/${a.slug}`} className="group grid grid-cols-[112px_1fr] md:grid-cols-[144px_1fr] items-center gap-6 py-7 first:pt-0">
                  <div className="img-zoom aspect-square bg-surface">
                    <Img src={a.image} alt={a.image_alt || a.title} sizes="144px" className="h-full w-full object-cover" />
                  </div>
                  <div>
                    <p className="t-label text-accent">{a.company}</p>
                    <h3 className="mt-2 text-xl md:text-[1.375rem] font-medium leading-[1.2] tracking-[-0.02em] group-hover:text-blue transition-colors duration-200">{a.title}</h3>
                    <p className="mt-2 t-small text-muted">{a.category}{a.published_at ? ` · ${formatDate(a.published_at)}` : ""}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
